import React, { useState, useEffect, useRef } from 'react';
import { useSetCurrentPage } from '../stores/useAppStore';
import { useToastStore } from '../stores/useToastStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import PageHeader from '../components/PageHeader';
import { quantitativeStrategyService } from '../services/quantitativeStrategyService';
import { StrategyOperation, POSITION_SIDE } from '@/services/enums';

interface Strategy {
  id: number | null;
  strategyId: number | null;
  strategyName: string;
  strategyCode: string;
  symbol: string | null;
  instType: string | null;
  timeframe: string | null;
  status: string;
  positionSize: number;
  leverage: number;
  totalTrades: number;
  winTrades: number;
  lossTrades: number;
  totalPnl: number;
  totalPnlPct: number;
  startedAt: string | null;
  stoppedAt: string | null;
  lastSignalAt: string | null;
  createdAt: string;
  updatedAt: string;
  currentPosition: {
    side: string;
    entryPrice: number;
    entryTime: string;
  } | null;
  latestBacktest: {
    totalReturnPct: number;
    winRate: number;
    sharpeRatio: number;
    maxDrawdown: number;
    totalTrades: number;
  } | null;
  instanceCount?: number;
}

const StrategySquare: React.FC = () => {
  const setCurrentPage = useSetCurrentPage();
  const showToast = useToastStore((state) => state.show);
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [actioningId, setActioningId] = useState<number | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    show: boolean;
    strategyId: number | null;
    strategyCode: string;
    action: StrategyOperation;
  } | null>(null);
  const [optimizingStrategy, setOptimizingStrategy] = useState<string | null>(null);
  const [optimizeStatus, setOptimizeStatus] = useState<any>(null);
  const optimizePollingRef = useRef<number | null>(null);
  const hasLoadedRef = useRef(false);

  // 加载策略列表
  const loadStrategies = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);
      const data = await quantitativeStrategyService.getStrategies();
      setStrategies(data as Strategy[]);
    } catch (error: any) {
      console.error('获取策略列表失败:', error);
      const message =
        error?.response?.status === 403
          ? error.message || '权限不足，无法查看策略'
          : error.message || '获取策略列表失败';
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };

  // 启动策略
  // const handleStartStrategy = async (id: number) => { // Unused variable
  //   if (actioningId) return;
  //
  //   try {
  //     setActioningId(id);
  //     await quantitativeStrategyService.startStrategy(id);
  //     showToast('策略已启动', 'success');
  //     await loadStrategies();
  //   } catch (error: any) {
  //     console.error('启动策略失败:', error);
  //     showToast(error.message || '启动策略失败', 'error');
  //   } finally {
  //     setActioningId(null);
  //   }
  // };

  // 停止策略
  const handleStopStrategy = async (id: number) => {
    if (actioningId) return;

    try {
      setActioningId(id);
      await quantitativeStrategyService.stopStrategy(id);
      showToast('策略已停止', 'success');
      await loadStrategies();
    } catch (error: any) {
      console.error('停止策略失败:', error);
      showToast(error.message || '停止策略失败', 'error');
    } finally {
      setActioningId(null);
    }
  };

  // 删除策略
  const handleDeleteStrategy = async (id: number) => {
    if (actioningId) return;

    try {
      setActioningId(id);
      await quantitativeStrategyService.deleteStrategy(id);
      showToast('策略已删除', 'success');
      await loadStrategies();
    } catch (error: any) {
      console.error('删除策略失败:', error);
      showToast(error.message || '删除策略失败', 'error');
    } finally {
      setActioningId(null);
      setConfirmDialog(null);
    }
  };

  // 运行回测（跳转到策略详情页）
  const runBacktest = (strategy: Strategy) => {
    // 使用 sessionStorage 传递策略代码
    sessionStorage.setItem('strategy_detail_code', strategy.strategyCode);
    setCurrentPage('strategy-detail');
  };

  // 优化参数
  const optimizeParams = async (strategy: Strategy) => {
    if (optimizingStrategy) return;

    try {
      setOptimizingStrategy(strategy.strategyCode);
      const { taskId } = await quantitativeStrategyService.startOptimize(strategy.strategyCode, {
        timeframe: '1H',
        method: 'random',
        objective: 'sharpe',
        maxIterations: 50,
        concurrency: 3,
      });

      showToast('参数优化已启动', 'success');

      // 开始轮询状态
      startOptimizePolling(taskId);
    } catch (error: any) {
      console.error('启动参数优化失败:', error);
      showToast(error.message || '启动参数优化失败', 'error');
      setOptimizingStrategy(null);
    }
  };

  // 轮询优化状态
  const startOptimizePolling = (taskId: string) => {
    const poll = async () => {
      try {
        const status = await quantitativeStrategyService.getOptimizeStatus(taskId);
        setOptimizeStatus(status);

        if (status.status === 'completed' || status.status === 'failed') {
          // 停止轮询
          if (optimizePollingRef.current) {
            clearInterval(optimizePollingRef.current);
            optimizePollingRef.current = null;
          }
          setOptimizingStrategy(null);

          if (status.status === 'completed') {
            showToast('参数优化完成', 'success');
            await loadStrategies(); // 重新加载策略列表
          } else {
            showToast('参数优化失败', 'error');
          }
        }
      } catch (error: any) {
        console.error('获取优化状态失败:', error);
      }
    };

    // 立即执行一次
    poll();

    // 每2秒轮询一次
    optimizePollingRef.current = window.setInterval(poll, 2000);
  };

  // 清理轮询
  useEffect(() => {
    return () => {
      if (optimizePollingRef.current) {
        clearInterval(optimizePollingRef.current);
      }
    };
  }, []);

  // 量化投资（启动策略）
  const startQuantitative = async (id: number) => {
    if (actioningId) return;

    try {
      setActioningId(id);
      await quantitativeStrategyService.startStrategy(id);
      showToast('量化投资已启动', 'success');
      await loadStrategies();
    } catch (error: any) {
      console.error('启动量化投资失败:', error);
      showToast(error.message || '启动量化投资失败', 'error');
    } finally {
      setActioningId(null);
      setConfirmDialog(null);
    }
  };

  useEffect(() => {
    if (hasLoadedRef.current) {
      return;
    }

    hasLoadedRef.current = true;
    loadStrategies();
  }, []);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const getStatusText = (status: string) => {
    const statusMap: { [key: string]: { text: string; color: string } } = {
      stopped: { text: '已停止', color: 'text-gray-400' },
      running: { text: '运行中', color: 'text-green-400' },
      paused: { text: '已暂停', color: 'text-yellow-400' },
      error: { text: '错误', color: 'text-red-400' },
    };
    return statusMap[status] || { text: status, color: 'text-white/60' };
  };

  return (
    <GradientBackground>
      <ParticleBackground />
      <div className="min-h-screen">
        {/* 页面头部 */}
        <PageHeader
          title="策略广场"
          onBack={() => setCurrentPage('personal-center')}
          rightContent={
            <button
              onClick={loadStrategies}
              disabled={loading}
              className="group flex items-center justify-center w-9 h-9 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white hover:bg-white/20 hover:border-white/30 transition-all duration-300 hover:scale-105 disabled:opacity-50"
            >
              <svg
                className={`w-4 h-4 transition-transform duration-300 ${loading ? 'animate-spin' : 'group-hover:rotate-180'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          }
        />

        {/* 内容区域 */}
        <div className="px-4 py-4 pt-20">
          {loading && strategies.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          ) : errorMessage ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="text-red-400 mb-4">{errorMessage}</div>
              <button
                onClick={loadStrategies}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
              >
                重试
              </button>
            </div>
          ) : strategies.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="text-white/60 mb-4">暂无策略</div>
              <button
                onClick={() => setCurrentPage('backtest')}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
              >
                创建策略
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {strategies.map((strategy, index) => {
                const statusInfo = getStatusText(strategy.status);
                const isRunning = strategy.status === 'running';
                const isActioning = strategy.id !== null && actioningId === strategy.id;
                const hasInstance = strategy.id !== null;

                return (
                  <div
                    key={strategy.id || `strategy-${strategy.strategyCode}-${index}`}
                    className="p-4 rounded-xl border backdrop-blur-sm bg-white/5 border-white/20 cursor-pointer hover:bg-white/10 transition-colors"
                    onClick={() => {
                      sessionStorage.setItem('strategy_detail_code', strategy.strategyCode);
                      setCurrentPage('strategy-detail');
                    }}
                  >
                    {/* 策略头部 */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-semibold hover:text-blue-400 transition-colors">
                            {strategy.strategyName}
                          </span>
                          <span className={`text-xs font-semibold ${statusInfo.color}`}>{statusInfo.text}</span>
                          {strategy.instanceCount !== undefined && strategy.instanceCount > 0 && (
                            <span className="text-xs text-white/40">({strategy.instanceCount}个实例)</span>
                          )}
                        </div>
                        <div className="text-white/60 text-xs">
                          {strategy.symbol
                            ? `${strategy.symbol} · ${strategy.timeframe} · 仓位: ${(strategy.positionSize * 100).toFixed(1)}%`
                            : '未创建实例'}
                        </div>
                      </div>
                    </div>

                    {/* 回测成绩 */}
                    {strategy.latestBacktest && (
                      <div className="mb-3 p-3 bg-white/5 rounded-lg">
                        <div className="text-white/60 text-xs mb-2">回测成绩</div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-white/60">收益率: </span>
                            <span
                              className={`font-semibold ${
                                strategy.latestBacktest.totalReturnPct >= 0 ? 'text-green-400' : 'text-red-400'
                              }`}
                            >
                              {formatPercent(strategy.latestBacktest.totalReturnPct)}
                            </span>
                          </div>
                          <div>
                            <span className="text-white/60">胜率: </span>
                            <span className="text-white font-semibold">
                              {/* winRate 在数据库中存储为百分比 (0-100)，如果 <= 1 则认为是小数格式需要转换 */}
                              {(strategy.latestBacktest.winRate > 1
                                ? strategy.latestBacktest.winRate
                                : strategy.latestBacktest.winRate * 100
                              ).toFixed(1)}
                              %
                            </span>
                          </div>
                          <div>
                            <span className="text-white/60">夏普比率: </span>
                            <span className="text-white font-semibold">
                              {strategy.latestBacktest.sharpeRatio.toFixed(2)}
                            </span>
                          </div>
                          <div>
                            <span className="text-white/60">最大回撤: </span>
                            <span className="text-red-400 font-semibold">
                              {formatPercent(-Math.abs(strategy.latestBacktest.maxDrawdown))}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 运行统计 */}
                    {strategy.totalTrades > 0 && (
                      <div className="mb-3 p-3 bg-white/5 rounded-lg">
                        <div className="text-white/60 text-xs mb-2">运行统计</div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-white/60">总交易: </span>
                            <span className="text-white font-semibold">{strategy.totalTrades}</span>
                          </div>
                          <div>
                            <span className="text-white/60">盈亏: </span>
                            <span
                              className={`font-semibold ${strategy.totalPnl >= 0 ? 'text-green-400' : 'text-red-400'}`}
                            >
                              {formatPercent(strategy.totalPnlPct)}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 当前持仓 */}
                    {strategy.currentPosition && (
                      <div className="mb-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <div className="text-blue-400 text-xs mb-1">当前持仓</div>
                        <div className="text-xs text-white/80">
                          {strategy.currentPosition.side === POSITION_SIDE.LONG ? '做多' : '做空'} · 入场价:{' '}
                          {strategy.currentPosition.entryPrice.toFixed(8)} ·
                          {formatDate(strategy.currentPosition.entryTime)}
                        </div>
                      </div>
                    )}

                    {/* 操作按钮 */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {!hasInstance ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCurrentPage('backtest');
                          }}
                          className="flex-1 px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg text-sm transition-colors"
                        >
                          创建实例
                        </button>
                      ) : !isRunning ? (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (strategy.id) {
                                setConfirmDialog({
                                  show: true,
                                  strategyId: strategy.id,
                                  strategyCode: strategy.strategyCode,
                                  action: 'start',
                                });
                              }
                            }}
                            disabled={isActioning || !strategy.id}
                            className="flex-1 px-3 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isActioning ? '启动中...' : '启动'}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (strategy.id) {
                                setConfirmDialog({
                                  show: true,
                                  strategyId: strategy.id,
                                  strategyCode: strategy.strategyCode,
                                  action: 'quantitative',
                                });
                              }
                            }}
                            disabled={isActioning || !strategy.id}
                            className="flex-1 px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            量化投资
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (strategy.id) {
                              setConfirmDialog({
                                show: true,
                                strategyId: strategy.id,
                                strategyCode: strategy.strategyCode,
                                action: 'stop',
                              });
                            }
                          }}
                          disabled={isActioning || !strategy.id}
                          className="flex-1 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isActioning ? '停止中...' : '停止'}
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          runBacktest(strategy);
                        }}
                        className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm transition-colors"
                      >
                        回测
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          optimizeParams(strategy);
                        }}
                        disabled={optimizingStrategy === strategy.strategyCode}
                        className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {optimizingStrategy === strategy.strategyCode ? '优化中...' : '优化'}
                      </button>
                      {hasInstance && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (strategy.id) {
                              setConfirmDialog({
                                show: true,
                                strategyId: strategy.id,
                                strategyCode: strategy.strategyCode,
                                action: 'delete',
                              });
                            }
                          }}
                          disabled={isActioning || !strategy.id}
                          className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          删除
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 优化状态提示 */}
        {optimizeStatus && optimizeStatus.status !== 'completed' && optimizeStatus.status !== 'failed' && (
          <div className="fixed bottom-4 left-4 right-4 z-50 bg-blue-500/20 backdrop-blur-md border border-blue-500/30 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="text-white font-semibold mb-1">参数优化进行中</div>
                <div className="text-white/60 text-sm">
                  策略: {optimizeStatus.strategyName} | 进度: {optimizeStatus.completedSymbols} /{' '}
                  {optimizeStatus.totalSymbols}
                  {optimizeStatus.currentSymbol && ` | 当前: ${optimizeStatus.currentSymbol}`}
                </div>
              </div>
              <button
                onClick={() => {
                  setOptimizeStatus(null);
                  if (optimizePollingRef.current) {
                    clearInterval(optimizePollingRef.current);
                    optimizePollingRef.current = null;
                  }
                }}
                className="px-3 py-1 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm transition-colors"
              >
                关闭
              </button>
            </div>
          </div>
        )}

        {/* 确认对话框 */}
        {confirmDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6 max-w-sm w-full mx-4">
              <h3 className="text-white font-semibold mb-4">
                {confirmDialog.action === 'quantitative'
                  ? '确认启动量化投资'
                  : confirmDialog.action === 'start'
                    ? '确认启动策略'
                    : confirmDialog.action === 'stop'
                      ? '确认停止策略'
                      : '确认删除策略'}
              </h3>
              <p className="text-white/80 text-sm mb-6">
                {confirmDialog.action === 'quantitative'
                  ? '量化投资将根据策略信号自动下单，请确认您已充分了解风险。'
                  : confirmDialog.action === 'start'
                    ? '启动后策略将开始监控市场并执行交易信号。'
                    : confirmDialog.action === 'stop'
                      ? '停止后策略将不再执行新的交易信号。'
                      : '删除后无法恢复，请谨慎操作。'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmDialog(null)}
                  className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={() => {
                    if (!confirmDialog.strategyId) return;
                    if (confirmDialog.action === 'quantitative' || confirmDialog.action === 'start') {
                      startQuantitative(confirmDialog.strategyId);
                    } else if (confirmDialog.action === 'stop') {
                      handleStopStrategy(confirmDialog.strategyId);
                    } else if (confirmDialog.action === 'delete') {
                      handleDeleteStrategy(confirmDialog.strategyId);
                    }
                  }}
                  disabled={!confirmDialog.strategyId}
                  className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                    confirmDialog.action === 'delete'
                      ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400'
                      : 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-400'
                  }`}
                >
                  确认
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </GradientBackground>
  );
};

export default StrategySquare;
